import { Controller } from "@hotwired/stimulus"

const WEIGHTS = {
  media: 30,
  components: 30,
  notes: 30,
  location: 4,
  roomSize: 3,
  genres: 3
}

const NOTES_THRESHOLDS = { tier1: 0, tier2: 100, tier3: 200 }

function tieredScore(count, max) {
  if (count === 0) return 0
  if (count === 1) return Math.round(max / 3)
  if (count === 2) return Math.round(max * 2 / 3)
  return max
}

export default class extends Controller {
  static targets = [
    "percentage", "progressBar",
    "mediaStatus", "componentsStatus", "notesStatus",
    "locationStatus", "sizeStatus", "genresStatus",
    "mobilePercentage", "mobileBar"
  ]

  static values = {
    photosContainer: String,
    videoContainer: String,
    componentsContainer: String
  }

  connect() {
    this.loadI18nTemplates()
    this.recalculate()
    this.setupObservers()
  }

  loadI18nTemplates() {
    const sidebar = this.element.querySelector("[data-completion-items-template]")
    if (sidebar) {
      this.i18n = {
        items: sidebar.dataset.completionItemsTemplate || "{count} items",
        chars: sidebar.dataset.completionCharsTemplate || "{count} chars",
        charsPlus: sidebar.dataset.completionCharsPlusTemplate || "{count}+ chars",
        empty: sidebar.dataset.completionEmptyLabel || "empty"
      }
    } else {
      this.i18n = { items: "{count} items", chars: "{count} chars", charsPlus: "{count}+ chars", empty: "empty" }
    }
  }

  disconnect() {
    if (this.photosObserver) this.photosObserver.disconnect()
    if (this.videoObserver) this.videoObserver.disconnect()
    if (this.componentsObserver) this.componentsObserver.disconnect()
  }

  setupObservers() {
    const observerConfig = { childList: true, subtree: true, attributes: true, attributeFilter: ["class", "value"] }

    // Observe photos container for additions/removals
    const photosPreview = this.element.querySelector("[data-dropzone-target='preview']")
    if (photosPreview) {
      this.photosObserver = new MutationObserver(() => this.recalculate())
      this.photosObserver.observe(photosPreview, observerConfig)
    }

    // Observe video container
    const videoContainers = this.element.querySelectorAll("[data-dropzone-single-value]")
    if (videoContainers.length > 0) {
      const videoPreview = videoContainers[0].querySelector("[data-dropzone-target='preview']")
      if (videoPreview) {
        this.videoObserver = new MutationObserver(() => this.recalculate())
        this.videoObserver.observe(videoPreview, observerConfig)
      }
    }

    // Observe components container for nested form add/remove
    const componentsSection = this.element.querySelector("[data-controller='nested-form']")
    if (componentsSection) {
      this.componentsObserver = new MutationObserver(() => this.recalculate())
      this.componentsObserver.observe(componentsSection, observerConfig)
    }
  }

  // Called on input events from text fields
  fieldChanged() {
    this.recalculate()
  }

  recalculate() {
    const media = this.countMedia()
    const components = this.countComponents()
    const notesLen = this.getNotesLength()

    const mediaScore = tieredScore(media, WEIGHTS.media)
    const componentsScore = tieredScore(components, WEIGHTS.components)
    const notesScore = this.calcNotesScore(notesLen)
    const locationScore = this.fieldPresent("room[location]") ? WEIGHTS.location : 0
    const sizeScore = this.fieldPresent("room[room_size]") ? WEIGHTS.roomSize : 0
    const genresScore = this.fieldPresent("room[genres]") ? WEIGHTS.genres : 0

    const total = mediaScore + componentsScore + notesScore + locationScore + sizeScore + genresScore

    this.updateDisplay(total, {
      media: mediaScore, mediaCount: media,
      components: componentsScore, componentsCount: components,
      notes: notesScore, notesLen,
      location: locationScore,
      size: sizeScore,
      genres: genresScore
    })
  }

  countMedia() {
    let count = 0

    // Count visible (non-destroyed) photos
    const photosPreview = this.element.querySelector("[data-dropzone-target='preview']")
    if (photosPreview) {
      const items = photosPreview.querySelectorAll("[data-sortable-item], .relative")
      items.forEach(item => {
        if (item.classList.contains("hidden")) return
        const destroyField = item.querySelector("[data-destroy-field]")
        if (destroyField && destroyField.value === "true") return
        count++
      })
    }

    // Check for video
    const videoContainers = this.element.querySelectorAll("[data-dropzone-single-value]")
    if (videoContainers.length > 0) {
      const videoPreview = videoContainers[0].querySelector("[data-dropzone-target='preview']")
      if (videoPreview) {
        const videoItems = videoPreview.querySelectorAll(".relative")
        videoItems.forEach(item => {
          if (item.classList.contains("hidden")) return
          const destroyField = item.querySelector("[data-destroy-field]")
          if (destroyField && destroyField.value === "true") return
          count++
        })
      }
    }

    return count
  }

  countComponents() {
    let count = 0
    const wrappers = this.element.querySelectorAll(".nested-form-wrapper")
    wrappers.forEach(wrapper => {
      if (wrapper.style.display === "none") return
      if (wrapper.classList.contains("hidden")) return
      const destroyField = wrapper.querySelector("input[name*='_destroy']")
      if (destroyField && destroyField.value === "1") return
      // Check that a component is actually selected
      const componentId = wrapper.querySelector("input[name*='component_id']")
      const componentName = wrapper.querySelector("input[name*='component_name']")
      if ((componentId && componentId.value) || (componentName && componentName.value)) {
        count++
      }
    })
    return count
  }

  getNotesLength() {
    const notesField = this.element.querySelector("textarea[name='room[notes]']")
    return notesField ? notesField.value.trim().length : 0
  }

  calcNotesScore(len) {
    if (len >= NOTES_THRESHOLDS.tier3) return WEIGHTS.notes
    if (len >= NOTES_THRESHOLDS.tier2) return Math.round(WEIGHTS.notes * 2 / 3)
    if (len > NOTES_THRESHOLDS.tier1) return Math.round(WEIGHTS.notes / 3)
    return 0
  }

  fieldPresent(name) {
    const field = this.element.querySelector(`input[name='${name}'], textarea[name='${name}']`)
    return field && field.value.trim().length > 0
  }

  updateDisplay(total, scores) {
    // Update percentage
    if (this.hasPercentageTarget) {
      this.percentageTarget.textContent = `${total}%`
    }

    // Update progress bar
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${total}%`
      // Color shifts: red < 30, yellow < 60, green >= 60
      if (total >= 60) {
        this.progressBarTarget.className = this.progressBarTarget.className.replace(/bg-\S+/g, "")
        this.progressBarTarget.classList.add("bg-success")
      } else if (total >= 30) {
        this.progressBarTarget.className = this.progressBarTarget.className.replace(/bg-\S+/g, "")
        this.progressBarTarget.classList.add("bg-warning")
      } else {
        this.progressBarTarget.className = this.progressBarTarget.className.replace(/bg-\S+/g, "")
        this.progressBarTarget.classList.add("bg-error")
      }
    }

    // Update section statuses
    this.updateStatus(this.mediaStatusTarget, scores.media, WEIGHTS.media, this.i18n.items.replace("{count}", scores.mediaCount))
    this.updateStatus(this.componentsStatusTarget, scores.components, WEIGHTS.components, this.i18n.items.replace("{count}", scores.componentsCount))
    this.updateStatus(this.notesStatusTarget, scores.notes, WEIGHTS.notes, this.notesHint(scores.notesLen))
    this.updateStatus(this.locationStatusTarget, scores.location, WEIGHTS.location)
    this.updateStatus(this.sizeStatusTarget, scores.size, WEIGHTS.roomSize)
    this.updateStatus(this.genresStatusTarget, scores.genres, WEIGHTS.genres)

    // Mobile bar
    if (this.hasMobilePercentageTarget) {
      this.mobilePercentageTarget.textContent = `${total}%`
    }
    if (this.hasMobileBarTarget) {
      this.mobileBarTarget.style.width = `${total}%`
    }
  }

  updateStatus(target, score, maxScore, hint) {
    if (!target) return
    const complete = score >= maxScore
    const partial = score > 0 && score < maxScore
    const icon = target.querySelector("[data-icon]")
    const hintEl = target.querySelector("[data-hint]")

    if (icon) {
      if (complete) {
        icon.innerHTML = "&#10003;"
        icon.className = "text-success text-sm font-bold"
      } else if (partial) {
        icon.innerHTML = "&#9711;"
        icon.className = "text-warning text-sm"
      } else {
        icon.innerHTML = "&#9711;"
        icon.className = "text-text-faint text-sm"
      }
    }

    if (hintEl && hint) {
      hintEl.textContent = hint
    }
  }

  notesHint(len) {
    if (len >= 200) return this.i18n.charsPlus.replace("{count}", "200")
    if (len > 0) return this.i18n.chars.replace("{count}", len)
    return this.i18n.empty
  }
}
